import React, { useState } from "react";
import { ListView } from "../../ListView";
import { getValue } from "../../../utils/object.utils";
import { StudentInfoItem } from "./StudentInfoItem";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedStudentForResult,
  updatePageNo,
} from "../../../Redux/Slices/studentResuilt.slice";
import { RootState } from "../../../Redux/store";
import { InputBase } from "@mui/material";
import { Colors } from "../../../Constants/colors";
import { FontFamily } from "../../../Constants/Enums/FontFamily.enums";
import {
  ALIGN_ITEMS,
  DISPLAY,
  POSITION,
  SPACING,
} from "../../../Constants/Enums/styles.enum";

export const StudentResultInfo = () => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const { selectedStudent, paginatedStudentList } = useSelector(
    (state: RootState) => ({
      selectedStudent: state.studentResult.selectedStudentForResult,
      pageNo: state.studentResult.pageNo,
      paginatedStudentList: state.studentResult.paginatedStudentResultList,
    }),
  );

  const studentList = getValue(paginatedStudentList, "result", []);
  const totalPage = getValue(paginatedStudentList, "lastPage", 0);

  // Filter students by name
  const filteredStudentList = studentList.filter((student: any) => {
    const fullName = getValue(student, "full_name", "").toLowerCase();
    const firstName = getValue(student, "first_name", "").toLowerCase();
    const lastName = getValue(student, "last_name", "").toLowerCase();
    const query = searchQuery.toLowerCase().trim();

    if (!query) return true;

    return (
      fullName.includes(query) ||
      firstName.includes(query) ||
      lastName.includes(query)
    );
  });

  const data = filteredStudentList.map((student: any) => {
    const isSelected =
      getValue(selectedStudent, "student_id") ===
      getValue(student, "student_id");

    return {
      child: <StudentInfoItem studentData={student} />,
      isSelected,
      onListSelected: () => {
        dispatch(setSelectedStudentForResult(student));
      },
    };
  });

  return (
    <div>
      {/* Filter Section */}
      <div
        style={{
          marginBottom: SPACING.S16,
          paddingBottom: SPACING.S12,
          borderBottom: `1px solid ${Colors.LightGrey}`,
        }}
      >
        <div
          style={{
            fontFamily: FontFamily.POPPINS_REGULAR,
            fontSize: "11px",
            color: Colors.Grey,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 600,
            marginBottom: SPACING.S10,
          }}
        >
          Search Students
        </div>

        {/* Search Input */}
        <div style={{ position: "relative", width: "100%" }}>
          <div
            style={{
              position: POSITION.ABSOLUTE,
              left: SPACING.S14,
              top: "50%",
              transform: "translateY(-50%)",
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
              pointerEvents: "none",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                fill={isFocused ? Colors.TangerineOrange : Colors.Grey}
              />
            </svg>
          </div>

          <InputBase
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Type student name to filter..."
            sx={{
              width: "100%",
              height: "48px",
              border: `2px solid ${isFocused ? Colors.TangerineOrange : Colors.LightGrey}`,
              borderRadius: "8px",
              padding: "12px 14px 12px 44px",
              fontSize: "14px",
              fontFamily: FontFamily.POPPINS_REGULAR,
              color: Colors.Black,
              backgroundColor: Colors.White,
              transition: "all 0.3s ease",
              boxShadow: isFocused
                ? `0 0 0 3px ${Colors.TangerineOrange}20`
                : "none",
              "&:hover": {
                borderColor: isFocused ? Colors.TangerineOrange : Colors.Grey,
                backgroundColor: Colors.VeryLightGrey,
              },
              "& input": {
                padding: 0,
                fontFamily: FontFamily.POPPINS_REGULAR,
                fontSize: "14px",
              },
              "& input::placeholder": {
                color: Colors.Grey,
                opacity: 0.7,
                fontStyle: "italic",
              },
            }}
          />

          {/* Clear Button */}
          {searchQuery && (
            <div
              onClick={() => setSearchQuery("")}
              style={{
                position: "absolute",
                right: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: "4px",
                borderRadius: "50%",
                backgroundColor: Colors.LightGrey,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = Colors.TangerineOrange;
                e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = Colors.LightGrey;
                e.currentTarget.style.transform = "translateY(-50%) scale(1)";
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                  fill={Colors.White}
                />
              </svg>
            </div>
          )}
        </div>

        {/* Results Count */}
        {searchQuery && (
          <div
            style={{
              marginTop: SPACING.S8,
              fontFamily: FontFamily.ROBOTO_REGULAR,
              fontSize: "12px",
              color: Colors.Grey,
            }}
          >
            {filteredStudentList.length} student
            {filteredStudentList.length !== 1 ? "s" : ""} found
          </div>
        )}
      </div>

      {/* Student List */}
      <ListView
        childrens={data}
        pagination={{
          totalPage,
          onPageChange: (pageNo) => {
            dispatch(updatePageNo(pageNo));
          },
        }}
        listStyle={{
          paddingRight: "0px",
        }}
      />

      {/* No Results Message */}
      {searchQuery && filteredStudentList.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: SPACING.S32,
            color: Colors.Grey,
            fontFamily: FontFamily.ROBOTO_REGULAR,
            fontSize: "14px",
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: "0 auto 16px", opacity: 0.5 }}
          >
            <path
              d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              fill={Colors.Grey}
            />
          </svg>
          <div style={{ fontWeight: 500, marginBottom: "4px" }}>
            No students found
          </div>
          <div style={{ fontSize: "12px" }}>
            Try searching with a different name
          </div>
        </div>
      )}
    </div>
  );
};
